import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { Router, NavigationExtras } from '@angular/router';

/**
 * Hero element position data for shared element transitions
 */
export interface HeroElementData {
  /** The element's bounding rect at the start of the transition */
  startRect: DOMRect;
  /** The icon/image source URL */
  iconSrc: string;
  /** The training type identifier */
  trainingType: string;
  /** Display name of the training */
  displayName: string;
  /** Target destination rect (optional, calculated on arrival) */
  endRect?: DOMRect;
}

/**
 * Transition state for tracking animation phases
 */
export type TransitionState = 'idle' | 'preparing' | 'animating' | 'complete';

/**
 * Transition Service
 * 
 * Manages shared element transitions between the Regiment component and App Details.
 * Coordinates the "hero" icon animation and state between views.
 */
@Injectable({
  providedIn: 'root'
})
export class TransitionService {

  /** Current hero element data */
  private heroData: HeroElementData | null = null;

  /** Current transition state */
  private transitionState$ = new BehaviorSubject<TransitionState>('idle');

  /** Emits when the hero animation should start */
  public heroAnimationStart$ = new Subject<HeroElementData>();

  /** Emits when the transition is complete */
  public transitionComplete$ = new Subject<void>();

  /** Whether a transition is currently active */
  public isTransitioning$ = new BehaviorSubject<boolean>(false);

  constructor(private router: Router) {}

  /**
   * Get current transition state
   */
  getTransitionState(): TransitionState {
    return this.transitionState$.value;
  }

  /**
   * Get current hero data
   */
  getHeroData(): HeroElementData | null {
    return this.heroData;
  }

  /**
   * Start a shared element transition to app details
   * @param element The clicked training item element
   * @param iconElement The icon element within the training item
   * @param trainingType The training type identifier (e.g., 'braingame', 'yahootie')
   * @param displayName The display name (e.g., 'Brain Game', 'Yahootie')
   * @param iconSrc The icon image source URL
   */
  async startTransition(
    element: HTMLElement,
    iconElement: HTMLElement,
    trainingType: string,
    displayName: string,
    iconSrc: string
  ): Promise<void> {
    // Get the starting position of the icon
    const startRect = iconElement.getBoundingClientRect();

    // Store hero data
    this.heroData = {
      startRect,
      iconSrc,
      trainingType,
      displayName
    };

    // Update state
    this.transitionState$.next('preparing');
    this.isTransitioning$.next(true);

    // Emit animation start event
    this.heroAnimationStart$.next(this.heroData);

    // Small delay to allow the animation overlay to set up
    await this.delay(50);

    // Update state to animating
    this.transitionState$.next('animating');

    // Navigate to the app details page
    const appName = this.getAppNameFromTrainingType(trainingType);
    const navigationExtras: NavigationExtras = {
      state: {
        data: {
          compliance: true,
          fromRegiment: true,
          heroData: this.heroData
        }
      }
    };

    await this.router.navigate(['/apps-menu', appName], navigationExtras);
  }

  /**
   * Signal that the destination has received the hero element
   * @param endRect The final position of the hero element
   */
  heroArrived(endRect?: DOMRect): void {
    if (this.heroData && endRect) {
      this.heroData.endRect = endRect;
    }

    this.transitionState$.next('complete');
    this.transitionComplete$.next();

    // Reset after animation completes
    setTimeout(() => {
      this.reset();
    }, 500);
  }

  /**
   * Reset transition state
   */
  reset(): void {
    this.heroData = null;
    this.transitionState$.next('idle');
    this.isTransitioning$.next(false);
  }

  /**
   * Cancel an in-progress transition
   */
  cancelTransition(): void {
    this.reset();
  }

  /**
   * Map training type to app name for routing
   */
  private getAppNameFromTrainingType(trainingType: string): string {
    const mapping: Record<string, string> = {
      'braingame': 'braingame',
      'yahootie': 'yahootie',
      'clubhouse': 'clubhouse',
      'happyplace': 'happyplace',
      'outsidetraining': 'outsidetraining'
    };
    return mapping[trainingType] || trainingType;
  }

  /**
   * Utility delay function
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Calculate the translation values for the hero animation
   * @param startRect Starting position
   * @param endRect Ending position
   */
  calculateHeroTranslation(startRect: DOMRect, endRect: DOMRect): { x: number; y: number; scale: number } {
    const x = endRect.left - startRect.left + (endRect.width - startRect.width) / 2;
    const y = endRect.top - startRect.top + (endRect.height - startRect.height) / 2;
    const scale = endRect.width / startRect.width;

    return { x, y, scale };
  }
}
